import React, { useState } from "react";
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

interface Props {
  data: CompetitionData;
  clubToHighlight?: number;
}

const ChartLineCompetitionProbability: React.FC<Props> = ({ data, clubToHighlight, hideProbability = false }) => {
  const rootStyles = getComputedStyle(document.documentElement);
  const primaryColorRgb = rootStyles.getPropertyValue("--primary-color-rgb").trim();
  const [metric, setMetric] = useState("trophyProbability");

  const startDate = new Date(data.startingDate);

  // Compute the maximum date from rankingHistory
  const endDate =
    data.rankingHistory?.reduce((max, r) => {
      const rDate = new Date(r.date);
      return rDate > max ? rDate : max;
    }, startDate) ?? startDate;

  // Map club ID → name
  const clubNames = data.participants?.reduce((acc, p) => {
    acc[p.id] = p.name;
    return acc;
  }, {});

  // Collect all unique dates from participant histories within date range
  const labelsSet = new Set();
  data.participants.forEach((club) => {
    club.history?.forEach((h) => {
      const date = new Date(h.date);
      if (date >= startDate && date <= endDate) {
        labelsSet.add(h.date);
      }
    });
  });

  const labels = data.rankingHistory.map((r) => r.date);
  const datasets = data.participants.map((club) => {
    const isHighlighted = clubToHighlight === club.id;
    const pointsData = data.rankingHistory.map((day) => {
      const ranking = day.rankings.find((r) => r.clubId === club.id);
      return ranking && !hideProbability ? ranking[metric] : null;
    });

    return {
      label: club.name,
      data: pointsData,
      borderWidth: 2,
      fill: false,
      tension: 0.3,
      borderColor: isHighlighted ? `rgba(${primaryColorRgb},1)` : `rgba(${primaryColorRgb},.2)`,
      pointRadius: isHighlighted ? 3 : 2,
      pointBackgroundColor: isHighlighted ? `rgba(${primaryColorRgb},1)` : `rgba(${primaryColorRgb},.5)`,
    };
  });

  const getData = () => ({ labels, datasets });

  return (
    <div className="position-absolute h-100 w-100 d-flex flex-column">
      {/* Top row with button aligned to right */}
      <div className="d-flex justify-content-end p-2">
        <ButtonSelect
          selected={metric}
          onChange={(v) => setMetric(v)}
          buttons={{ relegationProbability: "Relegation", promotionProbability: "Promotion", trophyProbability: "Trophy" }}
        />
      </div>

      {/* Chart fills remaining space */}
      <div className="flex-grow-1">
        <Line
          data={getData()}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
                position: "top",
                labels: {
                  usePointStyle: true,
                  pointStyle: "circle",
                  boxWidth: 6,
                  boxHeight: 6,
                  padding: 10,
                },
              },
              datalabels: { display: false },
            },
            scales: {
              x: { ticks: { beginAtZero: true }, grid: { display: false } },
              y: { ticks: { display: true }, grid: { display: false } },
            },
            layout: { padding: { top: 30 } },
          }}
        />
      </div>
    </div>
  );
};

export default ChartLineCompetitionProbability;
